import styled from '@emotion/styled';
import { Button, SxProps, Theme } from '@mui/material';
import { FC } from 'react';
import { Resource } from '@api/resources';
import { useResourceLoading } from '@api/utils/ApiLoadingStore';

interface ButtonContentContainerProps {
  ellipsisText?: boolean;
}

const ButtonContentContainer = styled.div(
  {
    display: 'flex',
    gap: '0.35rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ({ ellipsisText }: ButtonContentContainerProps) =>
    ellipsisText && {
      gap: 0,
      overflowX: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }
);

interface Props {
  id?: string;
  className?: string;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  color?:
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning';
  startIcon?: React.ReactNode;
  type?: 'submit' | 'reset' | 'button';
  disabled?: boolean;
  resourceLoading?: Resource | Resource[];
  autoFocus?: boolean;
  href?: string;
  stopPropagation?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  ellipsisText?: boolean;
  children: React.ReactNode;
}

export const QcsButton: FC<Props> = ({
  id,
  className,
  sx,
  fullWidth,
  variant,
  color,
  startIcon,
  type = 'button',
  disabled,
  resourceLoading,
  autoFocus,
  href,
  stopPropagation,
  onClick,
  onMouseEnter,
  children,
  ellipsisText,
}) => {
  const resourcesAreLoading = useResourceLoading(resourceLoading);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (stopPropagation) e.stopPropagation();
    onClick?.(e);
  };

  return (
    <Button
      id={id}
      className={className}
      sx={sx}
      fullWidth={fullWidth}
      variant={variant}
      color={color}
      startIcon={startIcon}
      type={type}
      disabled={disabled || resourcesAreLoading}
      autoFocus={autoFocus}
      href={href}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
    >
      <ButtonContentContainer ellipsisText={ellipsisText}>
        {children}
      </ButtonContentContainer>
    </Button>
  );
};
